<template>
  <nk-page commercialAccess="Salary" class="job-code" policyClaims="Salary_JobCode_Management">
    <template #badges>
      <nk-badge system="salary">
        <nk-badge-item type="system" modelValue="salary"></nk-badge-item>
        <nk-badge-item type="system" modelValue="salary" :title="$t('forms.JobCode')"></nk-badge-item>
      </nk-badge>
    </template>
    <div class="nk-card h-100 d-flex flex-column" startup>
      <div class="h-grow-lock">
        <nk-table v-model="selectedRow" @data-changed="dataChanged" :data="jobCodes" :options="tableOptions" ref="jobCodeList" tabindex="101" @row_dbClicked="rowDbClicked">
        </nk-table>
      </div>
    </div>
    <download-excel ref="exportExcel" class="btn btn-default d-none" :data="jobCodes" :fields="exportExcelFields()" :worksheet="$t('forms.JobCode')" type="xlsx" :name="`${$t('forms.JobCode')}.xlsx`"></download-excel>
    <input type="file" ref="excelFileInput" class="d-none" @change="changeInputExcelFile" accept=".xlsx,.xls" />
  </nk-page>
</template>
<script lang="ts">
  import Input from '~/components/general/nikan-input.vue'
  import $sc from '~/plugins/shortcut.collection.js'
  import dialog from '~/plugins/nikan-dialog-plugin'
  import validators from '~/statics/validators'
  import { defineComponent } from 'vue'
  import { JobCode } from '~/classes/salary/job-code'
  import { post } from '~/plugins/nikan-api'
  import Endpoints from '@nikan/endpoints'
  import { IResponseList } from '@/interfaces/base'
  import IsEmpty from 'lodash/isEmpty'
  import { TYPE } from 'vue-toastification'
  import { importExcel } from '@/plugins/helper'
  import emptyId from '@/statics/empty-id.js'
  import ProductImportErrors from '@/views/sale/products/product-import-errors.vue'

  interface IExcelRow {
    id?: string
    code: string
    name: string
    description?: string
    rowState?: string
  }
  export default defineComponent({
    name: 'job-code-selector',
    modal: {
      size: 'custom',
      width: 900,
      height: 600,
      stateUID: 'job-code-selector',
    },
    props: {
      options: { type: Object },
    },
    data() {
      return {
        jobCodes: [] as JobCode[],
        selectedRow: null as JobCode | null,
        tableOptions: {
          activeRowDisabled: true,
          editable: this.$nikan.checkPermission({ or: ['Salary_JobCode_Add', 'Salary_JobCode_Delete'] }),
          searchable: false,
          allowNewRow: true,
          alwaysEmit: true,
          buttons: {
            addBtn: {
              options: {
                visible: true,
                policyClaims: 'Salary_JobCode_Add',
              },
              displayOrder: 1,
            },
            deleteBtn: {
              options: {
                visible: true,
                policyClaims: 'Salary_JobCode_Delete',
              },
              displayOrder: 2,
            },
            importFromExcelBtn: {
              handler: this.importFromExcel,
              class: "no-outline",
              type: 'nk-btn',
              options: {
                isPrimary: false,
                hideShortcut: true,
                title: this.$t('sale.receipt-hawala.buttons.import-product-from-excel'),
                visible: true,
                tooltipEnabled: true,
                hideTitle: false,
              },
              displayOrder: 3,
            },
            downloadExcelSampleBtn: {
              handler: this.downloadSample,
              class: "no-outline",
              type: 'nk-btn',
              options: {
                isPrimary: false,
                // shortcut: $sc.find('search'),
                hideShortcut: true,
                title: this.$t('sale.receipt-hawala.buttons.download-excel-sample'),
                visible: true,
                // icon: 'plus',
                tooltipEnabled: true,
                hideTitle: false,
              },
              displayOrder: 4,
            },
            exportToExcelBtn: {
              handler: this.exportToExcel,
              class: "no-outline",
              type: 'nk-btn',
              options: {
                isPrimary: false,
                hideShortcut: true,
                title: this.$t('sale.receipt-hawala.buttons.export-to-excel'),
                visible: true,
                tooltipEnabled: true,
                hideTitle: false,
              },
              displayOrder: 5,
            }
          },
          displayOrderEnabled: false,
          prepareNewRow: () => new JobCode(),
          columns: [
            {
              name: 'rowNumber',
              title: this.$t('row-number'),
              searchable: true,
              sortable: true,
              editable: false,
              width: 20,
              entering: false,
              pinned: true,
              pinnable: false,
              resizable: false,
            },
            {
              name: 'code',
              title: this.$t('salary.personnel.job-code.f.code'),
              edit: {
                component: Input,
                options: {
                  mode: 'string',
                },
              },
              searchable: true,
              sortable: true,
              editable: true,
              width: 40,
              entering: true,
              pinnable: true,
              validators: validators('required'),
            },
            {
              name: 'name',
              title: this.$t('salary.personnel.job-code.f.name'),
              edit: {
                component: Input,
                options: {
                  mode: 'string',
                },
              },
              searchable: true,
              sortable: false,
              editable: true,
              width: 150,
              validators: validators('required'),
            },
          ],
        },
        pageButtons: {
          saveButton: {
            title: this.$t('save'),
            shortcut: $sc.find('save'),
            icon: 'save',
            visible: true,
            disabled: true,
            isPrimary: true,
            handle: () => {
              this.save().then(() => {
                this.$nikan.formUtility.switchMode(this.pageButtons, 'select',)
                this.$refs.jobCodeList.table_Focused()
              })
            },
            policyClaims: { or: ['Salary_JobCode_Add', 'Salary_JobCode_Delete'] },
          },
          selectButton: {
            title: this.$t('select'),
            shortcut: $sc.find('save'),
            icon: 'check',
            isPrimary: true,
            visible: true,
            disabled: false,
            handle: () => {

              if (IsEmpty(this.selectedRow))
                return this.$toast(this.$t('document.m.select-row'))

              this.resolve(this.selectedRow)
            }
          }
        }
      }
    },
    mounted() {
      this.prepareWindow(this.$t('salary.personnel.job-code.title'), [this.pageButtons.selectButton, this.pageButtons.saveButton])
      this.loadJobCodes()
    },
    methods: {
      save(): Promise<JobCode[]> {
        return new Promise((resolve, reject) => {
          this.$refs.jobCodeList
            .commitChanges()
            .then((jobCodes: (JobCode & { rowState: string })[]) => {

              const wait = this.$wait(this.$el)
              post(Endpoints.getUrl('jobCode.modify'), { items: jobCodes })
                .then(() => {
                  this.$toast(this.$t('salary.personnel.job-code.m.save-success'), { type: TYPE.SUCCESS })
                  this.loadJobCodes()
                  resolve(jobCodes)
                })
                .finally(() => {
                  wait.end()
                })


            })
            .catch((errorRows: { message: string } | { rowNumber: number }[]) => {
              if (Array.isArray(errorRows)) {
                var rowNumbers = errorRows.map((x, i) => x.rowNumber).join(',')
                dialog({
                  title: this.$t('salary.personnel.job-code.m.save-codes-title'),
                  body: this.$t('salary.personnel.job-code.m.error-rows', { rowNumbers }),
                  type: 'error',
                  isPrimary: true,
                })
              } else {
                dialog({
                  title: this.$t('salary.personnel.job-code.m.save-codes-title'),
                  body: errorRows.message,
                  type: 'error',
                  isPrimary: true,
                })
              }

              reject(new Error('invalid-rows'))
              this.defer(() => this.$refs.jobCodeList.$el.focus(), 100)
            })
        })
      },
      dataChanged() {
        this.$nikan.formUtility.switchMode(this.pageButtons, 'edit')
      },
      rowDbClicked(val: JobCode): void {
        if (this.grandParentType() === 'modal')
          this.resolve(val)
      },
      loadJobCodes(): void {
        const wait = this.$wait(this.$el)
        post(Endpoints.getUrl('jobCode.listDynamic'))
          .then((res: IResponseList<JobCode>) => {
            this.jobCodes = res.data.items
          })
          .finally(() => {
            wait.end()
          })
      },
      downloadSample() {
        const type = 'JobCodeSelecor'
        post(`/api/v1.0/system/Download/Download?fileName=nikan-${type.toLowerCase()}-excel-sample.xlsx`, null)
          .then((res: any) => {
            const url = window.URL.createObjectURL(
              new Blob([res.data], { type: res.headers['content-type'] })
            )
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = url
            // the filename you want
            a.download = `nikan-${type.toLowerCase()}-excel-sample.xlsx`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
          })
          .catch(() => { })
      },
      importFromExcel() {
        this.$refs.excelFileInput.value = null
        this.$refs.excelFileInput.click()
      },
      changeInputExcelFile(event: Event) {
        importExcel(event, this.exportExcelFields()).then(async res => {
          if (res) {
            const errors: IExcelRow[] = []

            const addError = (row: IExcelRow, description: string) => {
              errors.push({ code: row.code, name: row.name, description })
            }

            const rows = this.$refs.jobCodeList.getTableData().data
            const rowCodes: string[] = this.$refs.jobCodeList.getTableData().data.map((row: JobCode) => row.code)

            res.forEach((x: IExcelRow) => {

              if (!x.code || !x.name)
                addError(x, this.$t('salary.personnel.job-code.m.code-name-required'))

              if (x.code && rowCodes.includes(String(x.code)))
                addError(x, this.$t('salary.personnel.job-code.m.code-duplicate'))

              x.id = emptyId
              x.rowState = 'new'
              x.code = String(x.code)

              rows.push(x)
              rowCodes.push(x.code)
            })
            if (errors.length)
              await this.showModal({
                component: ProductImportErrors,
                data: errors,
                options: {
                  columnDefs: [
                    {
                      field: 'rowNumber',
                      headerName: this.$t('row-number'),
                      width: 50,
                    },
                    {
                      field: 'code',
                      headerName: this.$t('salary.personnel.job-code.f.code'),
                      width: 100,
                      // contentType: 'number'
                    },
                    {
                      field: 'name',
                      headerName: this.$t('salary.personnel.job-code.f.name'),
                      // width: 100,
                    },
                    {
                      field: 'description',
                      headerName: this.$t('sale.receipt-hawala.f.description'),
                      // width: 100,
                    },
                  ]
                }
              }).then(() => {
              }).catch(() => { })

            else {
              this.jobCodes = rows
              this.$toast(this.$t('salary.bank-payment-announce.m.file-imported-successfully'), { type: TYPE.SUCCESS })
              this.$nikan.formUtility.switchMode(this.pageButtons, 'edit')
            }

          }
        }).catch((e) => {
          throw new Error(e)
        })
      },
      exportToExcel() {
        if (this.jobCodes.length)
          this.defer(() => this.$refs.exportExcel.generate(), 0)
        else
          this.$toast(this.$t('m.no-data-for-export-to-excel'))
      },
      exportExcelFields() {
        return {
          [this.$t('salary.personnel.job-code.f.code')]: 'code',
          [this.$t('salary.personnel.job-code.f.name')]: 'name',
        }
      }
    },
  })
</script>
<style></style>
